"use client";
import React, { useRef, useEffect, useState } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import "./../styles/Elecinstallation.css";

import Header from './Header';
import Footer from './Footer';
import Beams from "./Beams";

import image1 from "../assets/oilfiltration2.jpeg";
import image2 from "../assets/filtration20A.jpg";
import image3 from "../assets/electrical14A.jpg";
import image4 from "../assets/filtration19A.jpg";
import image5 from "../assets/filtration16A.jpg";
import image6 from "../assets/filtration17A.webp";
import image7 from "../assets/oilfiltration1.jpeg";
import image8 from "../assets/filtrationprocess.webp";
import image9 from "../assets/filtration19A.jpg";



// --- Inner Timeline Component ---
const ElecDesignHeroSection = ({ data }) => {
  const timelineRef = useRef(null);
  const containerRef = useRef(null);
  const [timelineHeight, setTimelineHeight] = useState(0);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useEffect(() => {
    const calculateAndUpdateTimelineHeight = () => {
      if (timelineRef.current) {
        const newHeight = timelineRef.current.getBoundingClientRect().height;
        setTimelineHeight(prevHeight => {
          if (newHeight > 0 && (newHeight !== prevHeight || prevHeight === 0)) {
            return newHeight;
          }
          return prevHeight;
        });
      }
    };

    const handleLayoutReady = () => {
      calculateAndUpdateTimelineHeight();
      AOS.refresh();
    };

    const initialCalcTimer = setTimeout(calculateAndUpdateTimelineHeight, 50);

    if (document.readyState === "complete") {
      const readyTimer = setTimeout(handleLayoutReady, 100);
      return () => clearTimeout(readyTimer);
    } else {
      window.addEventListener("load", handleLayoutReady);
    }

    window.addEventListener("resize", handleLayoutReady);

    return () => {
      clearTimeout(initialCalcTimer);
      window.removeEventListener("load", handleLayoutReady);
      window.removeEventListener("resize", handleLayoutReady);
    };
  }, [data]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 50%"],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, timelineHeight]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div className="elec-design-hero-container" ref={containerRef}>
      <div className="elec-design-hero-header">
        <h2 className="elec-design-hero-title">Transformer Oil Filtration Services</h2>
        <p className="elec-design-hero-description">
          "Ensuring Reliability and Longevity for Your Transformers"
        </p>
      </div>

      <div ref={timelineRef} className="elec-design-hero-timeline">
        {data.map((item, index) => (
          <div key={index} className="elec-design-hero-step">
            <div className="elec-design-hero-sticky">
              <div className="elec-design-hero-marker-outer">
                <div className="elec-design-hero-marker-inner" />
              </div>
              <h3 className="elec-design-hero-step-title-desktop">{item.title}</h3>
            </div>

            <div className="elec-design-hero-step-content">
              <h3 className="elec-design-hero-step-title-mobile">{item.title}</h3>
              <img
                src={item.image}
                alt={item.title}
                className="elec-design-hero-step-image"
                data-aos="fade-left"
              />
              <div className="elec-design-hero-step-paragraph" data-aos="fade-left">
                {typeof item.paragraph === "string" ? (
                  <p>{item.paragraph}</p>
                ) : (
                  <>
                    {item.paragraph.text && <p>{item.paragraph.text}</p>}

                    {Array.isArray(item.paragraph.points) &&
                      item.paragraph.points.map((point, idx) =>
                        typeof point === "string" ? (
                          <p key={idx} className="elec-design-hero-step-point">
                            <span className="elec-design-hero-arrow-icon">→</span> {point}
                          </p>
                        ) : (
                          <div key={idx} className="elec-design-hero-step-subpoint">
                            <h4>
                              <span className="elec-design-hero-star-icon">★</span>
                              {point.heading.replace(/^★\s*/, "")}
                            </h4>
                            <p>{point.description}</p>
                          </div>
                        )
                      )}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}

        <div
          style={{ height: `${timelineHeight}px` }}
          className="elec-design-hero-progress-bar-wrapper"
        >
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="elec-design-hero-progress-bar"
          />
        </div>
      </div>
    </div>
  );
};

// --- Main Page Component ---
const Oilfiltration = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const timelineData = [
    {
      title: "Why Transformer Oil Filtration?",
      image: image7,
      paragraph: {
        text: "",
        points: [
          {
            heading: "★ Contaminant Removal:",
            description: "• Over time, transformer oil can accumulate moisture, gases, and particulates, degrading its insulating properties."
          },
          {
            heading: "★ Performance Optimization:",
            description: "• Clean oil enhances transformer performance, ensuring effective insulation and heat dissipation."
          },
          {
            heading: "★ Extended Lifespan:",
            description: "• Regular oil filtration prolongs transformer life, reducing costly failures and downtime."
          },
          {
            heading: "★ Environmental Compliance:",
            description: "• Maintained oil supports eco-compliance and sustainable power system operations."
          }
        ]
      }
    },
    {
      title: "Our Filtration Process",
      image: image8,
      paragraph: {
        text: "",
        points: [
          {
            heading: "★ Initial Assessment:",
            description: "• Analyzing oil condition to identify contamination and plan the filtration process."
          },
          {
            heading: "★ Oil Degassing:",
            description: "• Removing dissolved gases for improved dielectric strength and performance."
          },
          {
            heading: "★ Moisture Removal:",
            description: "• Vacuum drying techniques extract moisture, reducing electrical faults."
          },
          {
            heading: "★ Particulate Filtration:",
            description: "• Filtering out solid particles to maintain oil purity and efficiency."
          },
          {
            heading: "★ Oil Reconditioning:",
            description: "• Chemically restoring oil properties to original quality standards."
          },
          {
            heading: "★ Final Testing:",
            description: "• Conducting final tests to ensure industry-standard performance before reuse."
          }
        ]
      }
    },
      {
      title: "Why Choose Us?",
      image: image9,
      paragraph: {
        text: "",
        points: [
          {
            heading: "★    Expertise:",
            description: "•   Over 10 years of experience in transformer maintenance and oil filtration services."
          },
          {
            heading: "★    Advanced Technology:",
            description: "• Utilization of state-of-the-art equipment and techniques for superior filtration results."
          },
          {
            heading: "★    Certified Technicians:",
            description: "•Skilled and certified professionals dedicated to providing exceptional service."
          },
          {
            heading: "★  Comprehensive Support:",
            description: " • From initial assessment to final testing, we offer end-to-end support for all your transformer oil needs."
          },
          {
            heading: "★    Customer Satisfaction:",
            description: "•  Commitment to delivering reliable, efficient, and cost-effective solutions tailored to your needs."
          }
        ]
      }
    }
  ];

  return (
    <>
      <Header />
      <div className="elec-installation-page-container">
        <div className="elec-installation-page-header">
          <h1>Transformer Oil Filtration</h1>
          <p>
           We excel in delivering top-quality transformer oil filtration services that ensure the reliability and longevity of your transformers. Our advanced process, supported by 6 oil filtration machines (3000L, 6000L, and 10000L capacities), guarantees peak performance by removing contaminants and extending transformer life.

          </p>
          <p>
          Our qualified resources of men, machines, and materials—integrated with 2 vacuum pumps (S-10) and 4 oil storage tanks (3000L to 25000L capacities)—are deployed together for this critical preventive maintenance activity. The objective is to achieve optimal oil condition while the transformer remains in service, restoring essential insulating and dielectric properties.
          </p>
          <p>
           We offer cost-effective on-site oil reclamation services with processing capacities up to 10000 LPH, ensuring efficient oil handling, storage, and treatment—directly enhancing transformer performance and service life.
          </p>
        </div>

        <div className="elec-installation-cube-wrapper">
          <div className="elec-installation-cube-gallery">
            <img src={image1} alt="Engineering Slide 1" />
            <img src={image2} alt="Engineering Slide 2" />
            <img src={image3} alt="Engineering Slide 3" />
            <img src={image4} alt="Engineering Slide 4" />
            <img src={image5} alt="Engineering Slide 5" />
            <img src={image6} alt="Engineering Slide 6" />
          </div>
        </div>

        <div className="epc-beam-background">
          <Beams
            beamWidth={1.4}
            beamHeight={30}
            beamNumber={14}
            lightColor="#00CCFF"
            speed={20}
            noiseIntensity={0}
            scale={0.19}
            rotation={59}
          />
        </div>
      </div>

      <ElecDesignHeroSection data={timelineData} />
      <Footer />
    </>
  );
};

export default Oilfiltration;
